import { useState } from "react";
import { Link } from "wouter";
import { useListInsurancePolicies, useGetInsuranceSummary } from "@workspace/api-client-react";
import { CountUp, HoverLift } from "@/components/motion";
import { useOrg } from "@/lib/orgContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Filter, ShieldAlert, ShieldCheck, Shield, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { SampleBadge } from "@/components/SampleBadge";

function policyStatusVariant(status: string): "green" | "gold" | "red" | "muted" {
  if (status === "active") return "green";
  if (status === "expiring_soon") return "gold";
  if (status === "expired" || status === "cancelled") return "red";
  return "muted";
}

export default function Insurance() {
  const [status, setStatus] = useState<string>("all");
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";

  const { data: summaryData, isLoading: isLoadingSummary } = useGetInsuranceSummary();
  const { data: policies, isLoading: isLoadingPolicies } = useListInsurancePolicies({
    status: status === "all" ? undefined : (status as import("@workspace/api-client-react").ListInsurancePoliciesStatus),
  });

  const summary = summaryData ? {
    compliantCoaches: summaryData.filter((c) => c.overallStatus === "compliant").length,
    coachesWithIssues: summaryData.filter((c) => c.overallStatus === "issues").length,
    coachesMissingInsurance: summaryData.filter((c) => c.overallStatus === "missing").length,
  } : null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="page-header">
        <h1>Insurance Compliance</h1>
        <p>Track coverage policies across all coaches and flag any gaps before they become liabilities.</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-children">
        {isLoadingSummary || !summary ? (
          [1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl skeleton-shimmer" />)
        ) : (
          <>
            <HoverLift>
              <Card className="animate-count-up card-lift kpi-card-green h-full">
                <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Fully Compliant</CardTitle>
                  <div className="w-8 h-8 rounded-lg bg-field-green-subtle flex items-center justify-center">
                    <ShieldCheck className="h-4 w-4 text-field-green" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-bold kpi-value text-field-green">
                    <CountUp value={summary.compliantCoaches} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Coaches with full coverage</p>
                </CardContent>
              </Card>
            </HoverLift>
            <HoverLift>
              <Card className="animate-count-up card-lift kpi-card-gold h-full">
                <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Coverage Issues</CardTitle>
                  <div className="w-8 h-8 rounded-lg bg-gold-subtle flex items-center justify-center">
                    <ShieldAlert className="h-4 w-4 text-gold" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-bold kpi-value text-gold">
                    <CountUp value={summary.coachesWithIssues} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Expiring or incomplete policies</p>
                </CardContent>
              </Card>
            </HoverLift>
            <HoverLift>
              <Card className="animate-count-up card-lift kpi-card-red h-full">
                <CardHeader className="flex flex-row items-start justify-between pb-2 p-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Missing Coverage</CardTitle>
                  <div className="w-8 h-8 rounded-lg bg-signal-red-subtle flex items-center justify-center">
                    <Shield className="h-4 w-4 text-signal-red" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl font-bold kpi-value text-signal-red">
                    <CountUp value={summary.coachesMissingInsurance} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Requires immediate action</p>
                </CardContent>
              </Card>
            </HoverLift>
          </>
        )}
      </div>

      {/* Policy table */}
      <div className="rounded-xl border overflow-hidden bg-card">
        <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm font-semibold">All Policies</p>
          <div className="w-full sm:w-[200px]">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <Filter className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y">
          {isLoadingPolicies ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
            </div>
          ) : policies && policies.length > 0 ? (
            policies.map((policy) => (
              <div key={policy.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <Link href={`/coaches/${policy.coachId}`} className="font-semibold text-sm hover:underline truncate">
                      {policy.coachName}
                    </Link>
                    {policy.isSample && <SampleBadge />}
                  </div>
                  <Badge variant={policyStatusVariant(policy.status)}>
                    {policy.status.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground capitalize">{policy.policyType.replace("_", " ")}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground mb-0.5">Carrier</div>
                    <div className="font-medium">{policy.carrier}</div>
                    <div className="text-muted-foreground">{policy.policyNumber}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-0.5">Coverage</div>
                    <div className="font-medium kpi-value">${policy.coverageAmount.toLocaleString()}</div>
                    <div className="text-muted-foreground mt-1">{format(new Date(policy.endDate), "MMM d, yyyy")}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4">
              <EmptyState
                icon={Users}
                title="No insurance policies on file"
                description="Insurance policies are linked to coach profiles. Add coaches first, then record their coverage from the coach's profile page."
                action={isAdmin ? { label: "Add a coach", href: "/coaches/new" } : undefined}
              />
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Table className="min-w-[800px] table-refined">
            <TableHeader>
              <TableRow>
                <TableHead>Coach</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Carrier & Policy #</TableHead>
                <TableHead>Coverage Amount</TableHead>
                <TableHead>Policy Term</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingPolicies ? (
                [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[150px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[80px] rounded-md skeleton-shimmer" /></TableCell>
                  </TableRow>
                ))
              ) : policies && policies.length > 0 ? (
                policies.map((policy) => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Link href={`/coaches/${policy.coachId}`} className="hover:underline">{policy.coachName}</Link>
                        {policy.isSample && <SampleBadge />}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{policy.policyType.replace("_", " ")}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{policy.carrier}</span>
                        <span className="text-xs text-muted-foreground">{policy.policyNumber}</span>
                      </div>
                    </TableCell>
                    <TableCell className="kpi-value font-medium">${policy.coverageAmount.toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex flex-col text-xs">
                        <span className="font-medium">{format(new Date(policy.startDate), "MMM d, yyyy")}</span>
                        <span className="text-muted-foreground">to {format(new Date(policy.endDate), "MMM d, yyyy")}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={policyStatusVariant(policy.status)}>
                        {policy.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <EmptyState
                      icon={Users}
                      title="No insurance policies on file"
                      description="Insurance policies are linked to coach profiles. Add coaches first, then record their coverage from the coach's profile page."
                      action={isAdmin ? { label: "Add a coach", href: "/coaches/new" } : undefined}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
